import React from "react";
import cn from "./helpers/cn";

export type SwitchColor = "default" | "primary" | "secondary";
export type SwitchSize = "small" | "medium";
export type SwitchEdge = "start" | "end" | false;

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "size" | "type"> {
  checked?: boolean;
  defaultChecked?: boolean;
  color?: SwitchColor;
  size?: SwitchSize;
  edge?: SwitchEdge;
  disabled?: boolean;
  required?: boolean;
  disableRipple?: boolean;
  icon?: React.ReactNode;
  checkedIcon?: React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  /** Assinatura do MUI v4: recebe o evento + o novo valor `checked`. */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

// Dimensões fiéis ao MUI v4. Track e thumb são posicionados com coordenadas
// absolutas explícitas para evitar oscilação ao usar flex+translate.
//
// medium (root 58x38): track 34x14 centrada em (12,12); thumb 20x20 começa em
// (9,9) e desloca 20px à direita quando "on" — propositalmente sai 3px do trilho
// em cada extremo, igual ao visual MUI.
// small  (root 40x24): track 26x12 em (7,6); thumb 16x16 em (4,4), desloca 16px.
const sizeMap = {
  medium: {
    root: "w-[58px] h-[38px]",
    track: "absolute top-[12px] left-[12px] w-[34px] h-[14px] rounded-[7px]",
    thumb: "absolute top-[9px] left-[9px] w-5 h-5",
    thumbOff: "translate-x-0",
    thumbOn: "translate-x-5", // 20px
  },
  small: {
    root: "w-10 h-6",
    track: "absolute top-[6px] left-[7px] w-[26px] h-[12px] rounded-[6px]",
    thumb: "absolute top-[4px] left-[4px] w-4 h-4",
    thumbOff: "translate-x-0",
    thumbOn: "translate-x-4", // 16px
  },
} as const;

// Texto/cor da bolinha conforme estado e color prop.
function getColors(color: SwitchColor, checked: boolean) {
  const trackBase = "transition-opacity";
  const thumbBase = "transition-colors shadow";

  if (!checked) {
    // Estado off (MUI v4): trilho cinza opacity 0.38, thumb branco.
    return {
      track: `bg-slate-700 opacity-[0.38] ${trackBase}`,
      thumb: `bg-white ${thumbBase}`,
    };
  }

  // Estado on:
  if (color === "primary") {
    return {
      // Trilho na cor primary com opacidade reduzida (MUI v4).
      track: `bg-primary opacity-50 ${trackBase}`,
      thumb: `bg-primary ${thumbBase}`,
    };
  }
  if (color === "secondary") {
    return {
      track: `bg-secondary opacity-50 ${trackBase}`,
      thumb: `bg-secondary ${thumbBase}`,
    };
  }

  // default: igual a Material — track preto translúcido + thumb cinza claro.
  return {
    track: `bg-slate-700 opacity-50 ${trackBase}`,
    thumb: `bg-slate-200 ${thumbBase}`,
  };
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
  const {
    className,
    checked: checkedProp,
    defaultChecked = false,
    color = "secondary",
    size = "medium",
    edge = false,
    disabled = false,
    required = false,
    // Aceito por compatibilidade com a API do MUI v4; aqui não há ripple para desligar.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disableRipple,
    icon,
    checkedIcon,
    inputProps,
    inputRef,
    onChange,
    value,
    ...other
  } = props;

  // Suporte a uncontrolled (defaultChecked) ou controlled (checked).
  const isControlled = checkedProp !== undefined;
  const [innerChecked, setInnerChecked] = React.useState<boolean>(defaultChecked);
  const checked = isControlled ? !!checkedProp : innerChecked;

  const innerRef = React.useRef<HTMLInputElement | null>(null);

  const setRefs = (node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;

    if (!isControlled) setInnerChecked(newChecked);
    onChange?.(event, newChecked);
  };

  const sz = sizeMap[size];
  const colors = getColors(color, checked);

  // edge=start/end remove padding lateral pra alinhar o switch com a borda do container.
  const edgeClass = edge === "start" ? "-ml-3" : edge === "end" ? "-mr-3" : "";

  return (
    <span
      className={cn(
        // Track/thumb são posicionados em coordenadas absolutas dentro deste root,
        // então basta um inline-block relativo.
        "relative inline-block align-middle shrink-0",
        sz.root,
        edgeClass,
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {/* Checkbox real — invisível, mas é quem dispara o onChange semântico. */}
      <input
        ref={setRefs}
        type="checkbox"
        role="switch"
        checked={checked}
        defaultChecked={isControlled ? undefined : defaultChecked}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        value={value as React.InputHTMLAttributes<HTMLInputElement>["value"]}
        {...inputProps}
        {...other}
        className={cn(
          "peer absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10",
          disabled && "cursor-not-allowed"
        )}
      />

      {/* Trilho (track) */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none",
          sz.track,
          colors.track
        )}
      />

      {/* Bolinha (thumb) */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none rounded-full flex items-center justify-center",
          sz.thumb,
          colors.thumb,
          "transform transition-transform duration-150 ease-out",
          checked ? sz.thumbOn : sz.thumbOff,
          // Anel sutil ao receber foco via teclado (substitui o ripple do MUI).
          "peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-primary/40"
        )}
      >
        {checked ? checkedIcon : icon}
      </span>
    </span>
  );
});

Switch.displayName = "Switch";

export default Switch;
